import { dnd } from '../Core/DragAndDrop';
import DraggableAwareMixins from './DraggableAwareMixins';

export default {
  mixins: [DraggableAwareMixins],
  props: {
    assistiveText: {
      type: String,
    },
    item: {
      type: Object,
      required: true,
    },
    tag: {
      type: String,
      default: 'div',
    },
    scale: {
      type: Number,
      default: 1,
    },
    scaleDrag: {
      type: Number,
      default: 1,
    },
    z: {
      type: [String, Number],
      default: 'auto',
    },
    show: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draggableId: null,
      drag: {},
      left: 0,
      top: 0,
      isSelected: false,
      isDragging: false,
      zIndex: this.z,
      isEndTransition: false,
      hover: false,
    };
  },
  computed: {
    dragIn() {
      return !this.draggableInProgress && !(this.dragging || this.isSelected);
    },
    style() {
      return {
        transform: `translate(${this.left}px, ${this.top}px) ${
          this.hover ? 'scale(' + this.scaleDrag + ')' : ''
        }`,
        zIndex: this.zIndex, //this.draggableInProgress && this.draggableSource === this && !this.dragIn && !this.isEndTransition ? this.zIndex : 'auto'
      };
    },
    cssClasses() {
      let clazz = {
        drag: true,
      };

      if (this.drag.imageHover) {
        clazz = {
          ...clazz,
          [this.drag.image]: true,
          [this.drag.imageHover === this.drag.image
            ? this.drag.image + '-hover'
            : this.drag.imageHover]:
            this.hover ||
            (this.draggableInProgress && this.draggableSource === this),
        };
      }

      return {
        ...clazz,
        'drag-hover': this.hover,
        'drag-disable': this.disabled,
        'drag-in': this.dragIn,
        'drag-out':
          this.draggableInProgress &&
          this.draggableSource === this &&
          !this.dragIn,
        'drag-source':
          this.draggableInProgress && this.draggableSource === this,
      };
    },
    attributeBindings() {
      let attributes = {
        ...this.$attrs,
        role: 'link',
      };

      return {
        ...attributes,
        'aria-grabbed': !this.dragIn ? 'true' : 'false',
        'tabindex': this.drag.dropped || this.disabled ? -1 : 0,
      };
    },
  },
  mounted() {
    this.resetMouseState();
    this.draggableId = this._uid;

    this.$el.addEventListener('mousedown', this.handleDragStart);
    this.$el.addEventListener('touchstart', this.handleDragStart);
    this.$el.addEventListener('keydown', this.handleKeydown);
    this.$el.addEventListener('transitionend', () => {
      this.zIndex = this.z;
    });
  },
  methods: {
    handleDragStart(e) {
      e.preventDefault();

      if (this.disabled) {
        return true;
      }

      const target = e.target || e.srcElement;

      if (this.$el.contains(target) && !this.drag.dropped) {
        this.zIndex = this.z + 1;
        this.mouseClickPosition.mouseX = e.touches
          ? e.touches[0].pageX
          : e.pageX;
        this.mouseClickPosition.mouseY = e.touches
          ? e.touches[0].pageY
          : e.pageY;

        this.mouseClickPosition.top = this.top;
        this.mouseClickPosition.left = this.left;

        this.isSelected = true;
        this.isDragging = e.type !== 'keydown';
        this.drag = Object.assign({}, this.drag, { dragging: this.isDragging });

        dnd.startDraggable(this, e, this.drag);
        this.$emit('onDragStart', { ...this.drag, event: e });

        if (e.type !== 'keydown') {
          document.documentElement.addEventListener(
            'mousemove',
            this.handleDrag,
            true,
          );
          document.documentElement.addEventListener(
            'mouseup',
            this.handleUp,
            true,
          );

          document.documentElement.addEventListener(
            'touchmove',
            this.handleDrag,
            true,
          );
          document.documentElement.addEventListener(
            'touchend',
            this.handleUp,
            true,
          );
        }
      }
    },
    handleDrag(e) {
      e.preventDefault();
      if (this.draggableInProgress) {
        const pageX = e.touches ? e.touches[0].pageX : e.pageX;
        const pageY = e.touches ? e.touches[0].pageY : e.pageY;

        const delta = {
          x: (this.mouseClickPosition.mouseX - pageX) / this.scale,
          y: (this.mouseClickPosition.mouseY - pageY) / this.scale,
        };

        const left = this.mouseClickPosition.left - delta.x;
        const top = this.mouseClickPosition.top - delta.y;

        this.left = left;
        this.top = top;

        dnd.moveDraggable(e);
        this.$emit('onDrag', { drag: this.drag, left, top });
      }
    },
    handleUp(e) {
      dnd.endDraggable(e);
      this.resetMouseState();
      this.$emit('onDragEnd', { ...this.drag });

      document.documentElement.removeEventListener(
        'mousemove',
        this.handleDrag,
        true,
      );
      document.documentElement.removeEventListener(
        'mouseup',
        this.handleUp,
        true,
      );

      document.documentElement.removeEventListener(
        'touchmove',
        this.handleDrag,
        true,
      );
      document.documentElement.removeEventListener(
        'touchend',
        this.handleUp,
        true,
      );
    },
    handleKeydown(e) {
      if (e.keyCode === 32) {
        e.preventDefault();
        this.handleDragStart(e);
        this.zIndex = this.z;
      }
    },
    resetMouseState() {
      this.isDragging = false;
      this.mouseClickPosition = { mouseX: 0, mouseY: 0, left: 0, top: 0 };
      this.isSelected = false;
      this.left = 0;
      this.top = 0;
    },
  },
  watch: {
    item: {
      immediate: true,
      handler: function (drag) {
        this.drag = Object.assign({}, drag, { attempts: 0 });
      },
    },
    z(val) {
      if (val >= 0 || val === 'auto') {
        this.zIndex = val;
      }
    },
  },
  destroyed() {
    this.resetMouseState();
    dnd.resetDraggable();
    dnd.off('draggableStart');
    dnd.off('draggableEnd');
  },
};
